import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Droplets,
  Mountain,
  HardHat,
  ArrowDown,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

import { BRAND, IMG, CORE_SERVICES } from "../lib/site";
import { Reveal, MaskLines, FadeIn } from "../components/Reveal";
import {
  RequestQuoteButton,
  AskExpertButton,
  LeaveReviewButton,
} from "../components/Buttons";

const ICONS = { Droplets, Mountain, HardHat };

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page" className="bg-bb-bg">
      {/* ===== HERO ===== */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] flex items-center justify-center overflow-hidden grain"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 scale-110">
          <img
            src={IMG.hero}
            alt="Pumpjacks across the Delaware Basin at golden hour"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 cine-overlay" />
          <div className="absolute inset-0 vignette" />
        </motion.div>

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-10 max-w-5xl mx-auto px-5 md:px-10 text-center pt-24 pb-16"
        >
          <FadeIn delay={0.1}>
            <motion.img
              src={BRAND.shield}
              alt="Big Buck Services shield badge"
              className="mx-auto w-32 sm:w-40 md:w-52 drop-shadow-[0_10px_40px_rgba(0,0,0,0.7)]"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              data-testid="hero-shield-badge"
            />
          </FadeIn>

          <FadeIn delay={0.35}>
            <p className="mt-8 text-bb-gold font-mono2 text-[11px] sm:text-xs uppercase tracking-mega">
              Serving the Delaware/Permian Basin · Since 2014
            </p>
          </FadeIn>

          <MaskLines
            className="mt-5 font-display text-bb-silver leading-[0.88] text-5xl sm:text-7xl md:text-8xl uppercase"
            delay={0.45}
            lines={[
              <span key="1">Hauling.</span>,
              <span key="2" className="text-bb-gold">Dirt Work.</span>,
              <span key="3">Site Support.</span>,
            ]}
          />

          <FadeIn delay={1.1}>
            <p className="mt-7 mx-auto max-w-xl text-bb-silver/80 text-base md:text-lg leading-relaxed">
              Oilfield trucking and site services out of Hagerman, New Mexico.
              Locally owned, owner-operated, and proven across more than a decade
              in the field.
            </p>
          </FadeIn>

          <FadeIn delay={1.3}>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <RequestQuoteButton size="lg" testid="hero-request-quote-btn" />
              <AskExpertButton size="lg" testid="hero-ask-expert-btn" />
              <LeaveReviewButton size="lg" testid="hero-leave-review-btn" />
            </div>
          </FadeIn>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-bb-silver/50"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowDown className="h-6 w-6" />
        </motion.div>
      </section>

      {/* ===== POSITIONING ===== */}
      <section className="py-24 md:py-36 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-bb-gold font-mono2 text-xs uppercase tracking-mega mb-8">
              [ Who We Are ]
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-3xl sm:text-5xl md:text-6xl leading-[0.95] text-bb-silver max-w-4xl">
              A decade-plus of proven Delaware/Permian Basin performance.{" "}
              <span className="text-bb-silver/40">Locally owned and</span>{" "}
              <span className="text-bb-gold">owner-operated.</span>
            </h2>
          </Reveal>
        </div>
      </section>

      {/* ===== CORE SERVICES SNAPSHOT ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <Reveal>
              <p className="text-bb-gold font-mono2 text-xs uppercase tracking-mega mb-4">
                [ What We Do ]
              </p>
              <h2 className="font-display uppercase text-4xl md:text-6xl text-bb-silver leading-none">
                Core Services
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/services"
                data-testid="home-view-services-link"
                className="group inline-flex items-center gap-2 text-bb-silver/70 hover:text-bb-gold font-cond uppercase tracking-widest text-sm transition-colors"
              >
                All Services
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {CORE_SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.title} delay={i * 0.12} className="h-full">
                  <div
                    data-testid={`core-service-${i}`}
                    className="group h-full bg-bb-bg p-8 md:p-10 hover:bg-bb-surface transition-colors duration-500"
                  >
                    <div className="flex items-center justify-between mb-10">
                      <Icon
                        className="h-9 w-9 text-bb-gold"
                        strokeWidth={1.5}
                      />
                      <span className="font-mono2 text-xs text-bb-silver/30">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-display uppercase text-2xl md:text-3xl text-bb-silver mb-4">
                      {s.title}
                    </h3>
                    <p className="text-bb-silver/60 leading-relaxed">{s.copy}</p>
                    <div className="mt-8 h-px w-0 group-hover:w-full bg-bb-gold transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <section className="py-8 border-y border-white/10 bg-bb-surface/40 overflow-hidden">
        <Marquee speed={40} gradient={false} autoFill>
          {["Hauling", "Dirt Work", "Site Support", "Since 2014"].map((w, i) => (
            <div key={i} className="flex items-center">
              <span className="font-display uppercase text-4xl md:text-6xl text-bb-silver/90 px-8">
                {w}
              </span>
              <span className="text-bb-gold text-3xl md:text-5xl">✦</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ===== WHY BIG BUCK ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal className="order-2 lg:order-1">
            <p className="text-bb-gold font-mono2 text-xs uppercase tracking-mega mb-6">
              [ Why Big Buck ]
            </p>
            <h2 className="font-display uppercase text-4xl md:text-6xl text-bb-silver leading-[0.95] mb-10">
              Tough enough for the field. Straight enough to trust.
            </h2>
            <ul className="space-y-5">
              {[
                "Owner-operated. You talk to the person accountable for the work.",
                "Eleven-plus years unbroken with a leading Delaware Basin midstream client.",
                "Safety-first culture on every load and every pad.",
                "Local to Hagerman. Chaves, Eddy & Lea Counties are our backyard.",
              ].map((item, i) => (
                <li
                  key={i}
                  data-testid={`why-item-${i}`}
                  className="flex items-start gap-4 text-bb-silver/85 text-lg"
                >
                  <span className="mt-1 shrink-0 h-6 w-6 border border-bb-gold/60 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-bb-gold" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15} className="order-1 lg:order-2">
            <div className="group relative overflow-hidden aspect-[4/5] grain">
              <img
                src={IMG.canyonPump}
                alt="Oilfield equipment in the desert at dusk"
                className="w-full h-full object-cover hover-spotlight"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bb-bg via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6">
                <img src={BRAND.shield} alt="" className="w-16 opacity-90" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== CLOSING CTA BAND ===== */}
      <section className="relative py-28 md:py-40 overflow-hidden grain border-t border-white/10">
        <div className="absolute inset-0 z-0">
          <img
            src={IMG.stormPump}
            alt="Pumpjack silhouetted against stormy clouds"
            className="w-full h-full object-cover object-bottom"
          />
          <div className="absolute inset-0 bg-bb-bg/80" />
        </div>
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl text-bb-silver leading-[0.9]">
              Ready to <span className="text-bb-gold">move</span>?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-bb-silver/70 text-lg max-w-xl mx-auto">
              Tell us what you need hauled, graded, or built. You'll hear back
              from the owner. Fast.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <RequestQuoteButton size="lg" testid="closing-request-quote-btn" />
              <AskExpertButton size="lg" testid="closing-ask-expert-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
